/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { isLocalEnv } from "PhonosConstants";
import { createLogger } from "utilities/Logger";
import gatewayFetch from "utilities/gatewayFetch";
import { mapToServerFormat } from "utilities/ApiUtilities";
import FilterTypes from "constants/FilterTypes";
import { CurrentUser } from "reducers/CurrentUserReducer";
import {
  redactNumericPathSegments,
  redactUuids,
} from "utilities/StringUtilities";
import {
  EnhanceSpeechTrackErrorType,
  LimitReachedEnhanceSpeechTrackErrorType,
} from "components/EnhancedSpeech/EnhanceSpeechTrack";
import { trackPersonalizationEventFromAdobeAnalyticsEvent } from "api/CustomerPersonalization";

const logger = createLogger("Analytics");

export enum AnalyticsEvent {
  USER_DOWNLOADS_FILE = "user_downloads_file",
  USER_EXPORT_PROJECT_FAILS = "user_exports_project_fails",
  USER_EXPORT_PROJECT_SUCCESS = "user_exports_project_success",
  USER_EXPORTS_PROJECT = "user_exports_project",
  USER_EXPORTS_PROJECT_CLIP = "user_exports_project_clip",
  USER_EXPORTS_PROJECT_TRANSCRIPT = "user_exports_project_transcript",
  USER_EXPORTS_AUDIOGRAM = "user_exports_audiogram",
  USER_EXPORTS_AUDIOGRAM_CLIP = "user_exports_audiogram_clip",
  USER_EXPORTS_VIDEO = "user_exports_video",
  USER_EXPORTS_VIDEO_CLIP = "user_exports_video_clip",
  USER_EXPORTS_VIDEO_WITH_CAPTIONS = "user_exports_video_with_captions",
  USER_EXPORTS_VIDEO_CLIP_WITH_CAPTIONS = "user_exports_video_clip_with_captions",
  USER_MIC_CHECK = "user_mic_check",
  SESSION_LOAD = "session_load",
  USER_OPENS_MUSIC_COLLECTION_PANEL = "user_opens_music_collection_panel",
  STUDIO_USER_INSERTS_FROM_MUSIC_COLLECTION = "studio_user_inserts_from_music_collection",
  STUDIO_USER_INSERTS_CUSTOM_MUSIC = "studio_user_inserts_custom_music",
  START_RECORDING_ELAPSED_TIME = "start_recording_elapsed_time",
  USER_CLICKS_UPGRADE_BUTTON = "user_clicks_upgrade_button",
  ENHANCE_SPEECH_REJECTED_FOR_LEGAL_REASONS = "enhance_speech_rejected_for_legal_reasons",
  ENHANCE_UPLOAD_FAILED = "enhance_upload_failed",
  ENHANCE_SPEECH_INFLUENCER_TRY_NOW_CLICKED = "enhance_speech_influencer_try_now_clicked",
  ENHANCE_SPEECH_UPSELL_MODAL_OPENED = "enhance_speech_upsell_modal_opened",
  USER_VISIT_FREE_ENHANCE = "user_visited_free_enhance",
  USER_VISIT_UNIFIED_FREE_ENHANCE = "user_visited_unified_free_enhance",
  USER_VISIT_PREMIUM_ENHANCE = "user_visited_premium_enhance",
  USER_VISIT_MIC_CHECK = "user_visited_miccheck",
  USER_VISIT_RECORD_V2 = "user_visited_record_v2",
  USER_VISIT_FEATURES = "user_visited_features",
  EXPRESS_PREMIUM_CHECKOUT_STARTED = "express_premium_checkout_started",
  EXPRESS_PREMIUM_CHECKOUT_COMPLETED = "express_premium_checkout_completed",
  EXPRESS_PREMIUM_CHECKOUT_CANCELLED = "express_premium_checkout_cancelled",
  USER_TRIGGERS_VIDEO_UPSELL = "user_triggers_video_upsell",
  ESQA_PAYWALL_SEEN_SLIDER_UPGRADE = "esqa_paywall_seen_slider_upgrade",
  ESQA_PAYWALL_SEEN_EMPTY_STATE_UPGRADE = "esqa_paywall_seen_empty_state_upgrade",
  FREE_USER_DOWNLOAD_PROJECT_LENGTH_EXCEEDED = "free_user_download_project_length_exceeded",
  FREE_USER_DAILY_DOWNLOAD_PROJECTS_LIMIT = "free_user_daily_download_projects_limit",
  FREE_USER_TRIGGERS_DOWNLOAD_ORIGINAL_TRACKS_PAYWALL = "free_user_triggers_download_original_tracks_paywall",
  USER_INVITED_BY_EMAIL = "user_invited_by_email",
  USER_CREATED_INVITE_LINK = "user_created_invite_link",
  USER_REVOKED_INVITE_LINK = "user_revoked_invite_link",
  USER_JOINED_DAILY_MEETING = "user_joined_record_v2_meeting",
  USER_JOINED_PUBLIC_SESSION = "user_joined_public_session",
  USER_JOINED_PUBLIC_SESSION_V2 = "user_joined_public_session_v2",
  USER_JOINED_RECORD_V2_ROOM_WAS_FULL = "user_joined_record_v2_room_was_full",
  USER_STARTED_RECORDING_V2_OWNER = "user_started_recording_v2_owner",
  USER_STARTED_RECORDING_V2_PARTICIPANT = "user_started_recording_v2_participant",
  USER_STOPPED_RECORDING_V2_OWNER = "user_stopped_recording_v2_owner",
  BROWSER_ENFORCEMENT_PAGE_VIEWED = "browser_enforcement_page_viewed",
  USE_CASE_STARTED_RECORDING = "use_case_started_recording",
  USE_CASE_TRANSCRIBED_AUDIO = "use_case_transcribed_audio",
  USE_CASE_CREATED_AUDIOGRAM = "use_case_created_audiogram",
  USE_CASE_ADDED_CAPTIONS = "use_case_added_captions",
  USE_CASE_EDITED_AUDIO = "use_case_edited_audio",
  USE_CASE_REMOVE_MUSIC = "use_case_remove_music",
  USE_CASE_PAGE_FILE_SELECTED = "use_case_page_file_selected",
  USER_UPLOADED_MY_MUSIC = "user_uploaded_my_music",
  USER_BEGINS_FILE_UPLOAD = "user_begins_file_upload",
  USER_TRANSCRIBES_FILE = "user_transcribes_file",
  USER_CLICKED_ENHANCE_STUDIO_NUDGE = "user_clicked_enhance_studio_nudge",
  ENHANCE_SPEECH_ACTION_BAR_MAKE_EDITS_CLICKED = "enhance_speech_action_bar_make_edits_clicked",
  ENHANCE_SPEECH_ACTION_BAR_GET_TRANSCRIPT_CLICKED = "enhance_speech_action_bar_get_transcript_clicked",
  ENHANCE_SPEECH_ACTION_BAR_CONVERT_TO_VIDEO_CLICKED = "enhance_speech_action_bar_convert_to_video_clicked",
  ENHANCE_SPEECH_ACTION_BAR_ADD_CAPTIONS_CLICKED = "enhance_speech_action_bar_add_captions_clicked",
  USER_TOGGLED_VIDEO_CAPTIONS = "user_toggled_video_captions",
  JTBD_ENHANCE_HOME = "jtbd_enhance_home",
  JTBD_RECORD_HOME = "jtbd_record_home",
  JTBD_TRANSCRIBE_HOME = "jtbd_transcribe_home",
  JTBD_EDIT_HOME = "jtbd_edit_home",
  JTBD_MICCHECK_HOME = "jtbd_miccheck_home",
  JTBD_AUDIOGRAMS_HOME = "jtbd_audiograms_home",
  JTBD_CAPTIONS_HOME = "jtbd_captions_home",
  JTBD_REMOVE_MUSIC_HOME = "jtbd_remove_music_home",
  HOME_TEMPLATE_OPEN_PROJECT_CLICKED_1 = "home_template_open_project_clicked_1",
  HOME_TEMPLATE_OPEN_PROJECT_CLICKED_2 = "home_template_open_project_clicked_2",
  HOME_TEMPLATE_OPEN_PROJECT_CLICKED_3 = "home_template_open_project_clicked_3",
  USER_ENABLES_SPEAKER_DIARIZATION = "user_enables_speaker_diarization",
  USER_DISABLES_SPEAKER_DIARIZATION = "user_disables_speaker_diarization",
  STUDIO_RECORDING_SUCCESS = "studio_recording_success",
  STUDIO_USER_EDITS_TRANSCRIPT_TIMELINE_DELETE = "studio_user_edits_transcript_timeline_delete",
  STUDIO_USER_EDITS_TRANSCRIPT_TIMELINE_PASTE = "studio_user_edits_transcript_timeline_paste",
  STUDIO_USER_EDITS_TRANSCRIPT_TIMELINE_CUT = "studio_user_edits_transcript_timeline_cut",
  STUDIO_USER_INSERTS_PLACEHOLDER = "studio_user_inserts_placeholder",

  // Recording V2 specific events
  CAMERA_TOGGLED = "CAMERA_TOGGLED",
  MUTE_TOGGLED = "MUTE_TOGGLED",
  CAM_SWITCHED = "CAM_SWITCHED",
  MIC_SWITCHED = "MIC_SWITCHED",
  SELECTED_CAM_NOT_AVAILABLE = "SELECTED_CAM_NOT_AVAILABLE",
  SELECTED_MIC_NOT_AVAILABLE = "SELECTED_MIC_NOT_AVAILABLE",

  // Export customization events
  VIDEO_SIZE_SELECTED = "video_size_selected",
  VIDEO_RESOLUTION_SELECTED = "video_resolution_selected",
  VIDEO_CAPTIONS_THEME_SELECTED = "video_captions_theme_selected",
  VIDEO_SHOW_SPEAKER_NAME_TOGGLED = "video_show_speaker_name_toggled",
  VIDEO_SHOW_PROJECT_TITLE_TOGGLED = "video_show_project_title_toggled",
  AUDIOGRAM_SIZE_SELECTED = "audiogram_size_selected",
  AUDIOGRAM_THEME_SELECTED = "audiogram_theme_selected",
  AUDIOGRAM_CUSTOM_BACKGROUND_SELECTED = "audiogram_custom_background_selected",
  AUDIOGRAM_SHOW_SPEAKER_NAME_TOGGLED = "audiogram_show_speaker_name_toggled",
  AUDIOGRAM_SHOW_PROJECT_TITLE_TOGGLED = "audiogram_show_project_title_toggled",

  // Studio export completion events
  STUDIO_EXPORT_MP3 = "studio_export_mp3",
  STUDIO_EXPORT_WAV = "studio_export_wav",
  STUDIO_EXPORT_TXT = "studio_export_txt",
  STUDIO_EXPORT_PDF = "studio_export_pdf",
  STUDIO_EXPORT_DOCX = "studio_export_docx",
  STUDIO_EXPORT_SRT = "studio_export_srt",
  STUDIO_EXPORT_RECORDINGS = "studio_export_recordings",

  STUDIO_PAYWALL_SEEN_VIDEO_RESOLUTION = "studio_paywall_seen_video_resolution",
  STUDIO_PAYWALL_SEEN_CAPTION = "studio_paywall_seen_caption",
  STUDIO_PAYWALL_SEEN_CAPTION_THEME = "studio_paywall_seen_caption_theme",
  STUDIO_PAYWALL_SEEN_VIDEO_REMOVE_BRANDING = "studio_paywall_seen_video_remove_branding",
  STUDIO_PAYWALL_SEEN_AUDIOGRAM_THEME = "studio_paywall_seen_audiogram_theme",
  STUDIO_PAYWALL_SEEN_AUDIOGRAM_CUSTOM_BACKGROUND = "studio_paywall_seen_audiogram_custom_background",
  STUDIO_PAYWALL_SEEN_AUDIOGRAM_REMOVE_BRANDING = "studio_paywall_seen_audiogram_remove_branding",
  STUDIO_PAYWALL_SEEN_EXPORT_PANEL = "studio_paywall_seen_export_panel",
  STUDIO_PAYWALL_SEEN_STRENGTH_SLIDER_VIDEO = "studio_paywall_seen_strength_slider_video",
  STUDIO_PAYWALL_SEEN_STRENGTH_SLIDER_NO_VIDEO = "studio_paywall_seen_strength_slider_no_video",
}

// Don't add new position param values without consulting
// https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=dva&title=DVA+SDM+Analytics+Lexicon#MainCards--618193127
export enum ExpressCheckoutButtonPosition {
  FREE_ENHANCED_SPEECH_BENTO_BOX = "click:free-enhanced-speech-bento-box",
  FREE_ENHANCED_SPEECH_EMPTY_STATE = "click:free-enhanced-speech-empty-state",
  FEATURES_BENTO_BOX = "click:features-bento-box",
  FEATURES_PREMIUM_PANEL = "click:features-premium-panel-button",
  FEATURES_FOOTER = "click:features-footer-button",
  FEATURES_HEADER = "click:features-header-button",
  FEATURES_PROFILE_DROPDOWN = "click:features-profile-dropdown-button",
  FREE_ENHANCE_UPGRADE_ROW = "click:free-enhance-upgrade-row",
  UPGRADE_BUTTON_POPUP_HEADER = "click:upgrade-button-popup-header",
  UPGRADE_BUTTON_POPUP_PROFILE_DROPDOWN = "click:upgrade-button-popup-profile-dropdown",
  ENHANCE_FINISHED_UPSELL = "click:free-enhance-finished-upsell",
  STUDIO_DOWNLOAD_ORIGINAL_TRACKS_UPSELL = "click:free-studio-download-original-tracks-upsell",
  STUDIO_DOWNLOAD_SIZE_LIMIT_UPSELL = "click:free-studio-download-size-limit-upsell",
  STUDIO_DOWNLOAD_FREQUENCY_LIMIT_UPSELL = "click:free-studio-download-frequency-limit-upsell",
  STUDIO_DOWNLOAD_PANEL = "click:free-studio-download-panel-upsell",
  STUDIO_STRENGTH_SLIDER_UPSELL = "click:free-studio-strength-slider-upsell",
  STUDIO_UPGRADE_MODAL = "click:free-studio-upgrade-modal",
  ENHANCE_LIMIT_FILE_SIZE_LIMIT = "click:free-enhance-limit:file_size_limit",
  ENHANCE_LIMIT_FILE_DURATION_LIMIT = "click:free-enhance-limit:file_duration_limit",
  ENHANCE_LIMIT_CONCURRENCY_LIMIT = "click:free-enhance-limit:concurrency_limit",
  ENHANCE_LIMIT_CUMULATIVE_DURATION_LIMIT = "click:free-enhance-limit:cumulative_duration_limit",
  ENHANCE_LIMIT_VIDEO_NOT_ACCEPTED = "click:free-enhance-limit:video_not_accepted",
  ENHANCE_LIMIT_MULTI_FILE_LIMIT = "click:free-enhance-limit:multi_file_limit",
  ENHANCE_STRENGTH_ADJUSTERS = "click:free-enhance-limit:strength-slider",
  PAGE_LOAD = "page_load",
  AUDIOGRAM_REMOVE_BRANDING = "audiogram_remove_branding",
  AUDIOGRAM_THEME_UPGRADE = "audiogram_theme_upgrade",
  AUDIOGRAM_CUSTOM_BACKGROUND_IMAGE_UPGRADE = "audiogram_custom_background_image_upgrade",
  VIDEO_RESOLUTION_UPGRADE = "video_resolution_upgrade",
  VIDEO_REMOVE_BRANDING = "video_remove_branding",
  VIDEO_CAPTION_THEME_UPGRADE = "video_caption_theme_upgrade",
}

export enum PostEnhanceStudioNudgeButtonPosition {
  GET_TRANSCRIPT_STUDIO_NUDGE = "click:get-transcript-studio-nudge",
  MAKE_EDITS_STUDIO_NUDGE = "click:make-edits-studio-nudge",
  ADD_MUSIC_STUDIO_NUDGE = "click:add-music-studio-nudge",
}

export const LimitReachedErrorToCheckoutPosition: {
  [T in LimitReachedEnhanceSpeechTrackErrorType]: ExpressCheckoutButtonPosition;
} = {
  [EnhanceSpeechTrackErrorType.CONCURRENCY_LIMIT]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_CONCURRENCY_LIMIT,
  [EnhanceSpeechTrackErrorType.CUMULATIVE_DURATION_LIMIT]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_CUMULATIVE_DURATION_LIMIT,
  [EnhanceSpeechTrackErrorType.FILE_DURATION_LIMIT]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_FILE_DURATION_LIMIT,
  [EnhanceSpeechTrackErrorType.FILE_SIZE_LIMIT]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_FILE_SIZE_LIMIT,
  [EnhanceSpeechTrackErrorType.VIDEO_NOT_ACCEPTED]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_VIDEO_NOT_ACCEPTED,
  [EnhanceSpeechTrackErrorType.MULTI_FILE_LIMIT]:
    ExpressCheckoutButtonPosition.ENHANCE_LIMIT_MULTI_FILE_LIMIT,
};

export enum NewRelicEvent {
  USER_STARTED_RECORDING_V2_OWNER = "USER_STARTED_RECORDING_V2_OWNER",
  ALL_PARTICIPANTS_MUTED_FOR_RECORDING = "ALL_PARTICIPANTS_MUTED_FOR_RECORDING",
  CLIP_RECOVERY_UPLOAD_STATE_UPDATED = "CLIP_RECOVERY_UPLOAD_STATE_UPDATED",
  PARTICIPANT_RECORDING_SESSION_UPLOAD_FAILED = "PARTICIPANT_RECORDING_SESSION_UPLOAD_FAILED",
  RECORDING_UPLOADED = "RECORDING_UPLOADED",
  SELF_INITIATED_STOP_RECORDING = "SELF_INITIATED_STOP_RECORDING",
  ABANDON_RECORDING = "ABANDON_RECORDING",
  STUDIO_VIDEO_PLAYER_DRIFT = "STUDIO_VIDEO_PLAYER_DRIFT",
  RECORDING_BLOCKED_BY_MIC_PERMISSIONS = "RECORDING_BLOCKED_BY_MIC_PERMISSIONS",
  BROWSER_ENFORCEMENT_PAGE_VIEWED = "BROWSER_ENFORCEMENT_PAGE_VIEWED",
  RECORDING_SESSION_ALREADY_FINALIZED = "RECORDING_SESSION_ALREADY_FINALIZED",
  UPLOAD_FILE_FOR_CLIP_STARTED = "UPLOAD_FILE_FOR_CLIP_STARTED",
  UPLOAD_FILE_FOR_CLIP_REJECTED_CLIENT_SIDE = "UPLOAD_FILE_FOR_CLIP_REJECTED_CLIENT_SIDE",
  UPLOAD_FILE_FOR_CLIP_COMPLETED = "UPLOAD_FILE_FOR_CLIP_COMPLETED",
  CAMERA_TOGGLED = "CAMERA_TOGGLED",
  MUTE_TOGGLED = "MUTE_TOGGLED",
  CAM_SWITCHED = "CAM_SWITCHED",
  MIC_SWITCHED = "MIC_SWITCHED",
  SELECTED_CAM_NOT_AVAILABLE = "SELECTED_CAM_NOT_AVAILABLE",
  SELECTED_MIC_NOT_AVAILABLE = "SELECTED_MIC_NOT_AVAILABLE",
  DAILY_WEBRTC_NETWORK_TEST = "DAILY_WEBRTC_NETWORK_TEST",
  USER_ENABLES_SPEAKER_DIARIZATION = "USER_ENABLES_SPEAKER_DIARIZATION",
  USER_DISABLES_SPEAKER_DIARIZATION = "USER_DISABLES_SPEAKER_DIARIZATION",
}

export function generateProjectMetadata(project: any, duration: Number): any {
  const metadata: any = {
    projectId: project.id,
    duration,
  };

  for (const key of Object.values(FilterTypes)) {
    const value = project.filters && project.filters[key]?.enabled;
    metadata[key] = value || false;
  }

  return metadata;
}

export interface StoredEvent {
  event: AnalyticsEvent;
  params: Record<string, any>;
}

interface StoredEventHandler {
  handleEvent: (event: StoredEvent) => void;
  processEventQueue: (queue: StoredEvent[]) => StoredEvent[];
}

class AnalyticsLogger {
  private events: StoredEvent[] = [];
  private eventHandler: StoredEventHandler | undefined;
  private currentUser: CurrentUser | null = null;

  public setEventHandler(handler: StoredEventHandler): void {
    this.eventHandler = handler;
    this.handleEvents();
  }

  public setCurrentUser(currentUser: CurrentUser | null): void {
    this.currentUser = currentUser;
  }

  public logEvent(
    event: AnalyticsEvent,
    additionalParams: Record<string, any> = {}
  ): void {
    if (isLocalEnv) {
      logger.info(
        `Tracking event: ${event} with params: ${JSON.stringify(additionalParams)}`
      );
    }

    if (!this.currentUser) {
      console.warn(
        `Attempted to send analytics event ${event} for logged out user.`
      );
      return;
    }

    // Also send events to C.IO, Fullstory and AJO
    trackPersonalizationEventFromAdobeAnalyticsEvent(event, additionalParams);

    const location = redactNumericPathSegments(
      redactUuids(window.location.pathname)
    );

    this.events.push({
      event: event,
      params: {
        ...additionalParams,
        location: location,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        has_premium_access: this.currentUser?.hasPremiumAccess,
        user_is_logged_in: !this.currentUser?.isAnonymous,
        timestamp: new Date().toISOString(),
      },
    });
    this.handleEvents();
  }

  private handleEvents(): void {
    if (this.eventHandler === undefined) {
      return;
    }
    for (const event of this.events) {
      this.eventHandler.handleEvent(event);
    }
    this.events = this.eventHandler.processEventQueue(this.events);
  }
}

export namespace Analytics {
  export const instance = new AnalyticsLogger();

  export const nullHandler: StoredEventHandler = {
    handleEvent: (): void => {},
    processEventQueue: (queue) => queue,
  };

  export const loggingHandler: StoredEventHandler = {
    handleEvent: (event): void => {
      gatewayFetch(`/api/v1/analytics_event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          mapToServerFormat({
            eventName: event.event,
            additionalParams: event.params,
          })
        ),
      }).then(() => {});
    },
    processEventQueue: (queue) => [],
  };
}

export default Analytics;
